import React, { useState } from "react";
import { useLanguage } from "../lib/LanguageContext";
import useSEO from "../lib/useSEO";
import { apiClient, errorMessage } from "../lib/api";
import { toast } from "sonner";

const SERVICES = ["Property Management", "Estate / Home Oversight", "Preventive Maintenance", "Hurricane & Storm Preparation", "Maintenance & Construction", "Housekeeping", "Concierge Services", "Arrival Preparation", "Vendor Coordination", "Luxury Rental Oversight", "Other"];
const COMMUNITIES = ["Palmilla", "Villas del Mar", "Querencia", "El Dorado", "Chileno Bay", "Maravilla", "Esperanza", "Other"];
// Values are canonical English (backend derives display name from these); labels are localized.
const DISPLAY_PREFS = [
    { value: "Full name", key: "prefFull" },
    { value: "First name + last initial", key: "prefFirstLast" },
    { value: "First name only", key: "prefFirst" },
    { value: "Private Homeowner", key: "prefPrivate" },
    { value: "Please contact me before displaying my name", key: "prefContact" },
];

const track = (name) => { try { window.gtag && window.gtag("event", name); } catch (e) {} };
const inputCls = "w-full bg-white border border-navy/20 px-4 py-3 text-navy text-sm focus:outline-none focus:border-gold transition-colors";
const labelCls = "overline text-navy/55 mb-2 block";

export default function ShareExperience() {
    const { t } = useLanguage();
    useSEO({
        title: t("sharePage.seoTitle"),
        description: t("sharePage.seoDescription"),
        path: "/share-your-experience",
        robots: "noindex, follow",
    });
    const [form, setForm] = useState({ name: "", email: "", phone: "", community: "", service_category: "", testimonial: "", display_preference: "Private Homeowner", show_community: false, consent: false, company: "" });
    const [photoPath, setPhotoPath] = useState("");
    const [photoName, setPhotoName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { toast.error(t("sharePage.photoTooLarge")); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await apiClient.post("/testimonials/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPhotoPath(data.photo_path); setPhotoName(file.name);
            toast.success(t("sharePage.photoAdded"));
        } catch (err) { toast.error(errorMessage(err) || t("sharePage.uploadFailed")); }
        finally { setUploading(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) { toast.error(t("sharePage.consentError")); return; }
        setLoading(true);
        try {
            await apiClient.post("/testimonials", { ...form, photo_path: photoPath });
            track("testimonial_submission_completed");
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) { toast.error(errorMessage(err) || t("sharePage.genericError")); }
        finally { setLoading(false); }
    };

    if (done) {
        return (
            <div className="min-h-[70vh] bg-cream flex items-center justify-center px-6 py-24" data-testid="testimonial-thankyou">
                <div className="max-w-xl text-center">
                    <p className="overline text-gold mb-4">{t("sharePage.eyebrow")}</p>
                    <h1 className="font-serif text-4xl sm:text-5xl text-navy mb-6">{t("sharePage.thankyouHeading")}</h1>
                    <p className="text-navy/70 text-base leading-relaxed">{t("sharePage.thankyouBody")}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-cream min-h-screen py-20 sm:py-28 px-6" data-testid="share-experience-page">
            <div className="max-w-2xl mx-auto">
                <p className="overline text-gold mb-4">{t("sharePage.eyebrow")}</p>
                <h1 className="font-serif text-4xl sm:text-5xl text-navy mb-6" data-testid="share-heading">{t("sharePage.heading")}</h1>
                <p className="text-navy/70 text-base leading-relaxed mb-12 max-w-xl">{t("sharePage.intro")}</p>

                <form onSubmit={submit} className="space-y-7" data-testid="testimonial-form" onFocus={() => track("testimonial_submission_started")}>
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" aria-hidden="true" />

                    <div><label className={labelCls}>{t("sharePage.nameLabel")} *</label><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="t-name" /></div>
                    <div><label className={labelCls}>{t("sharePage.emailLabel")} *</label><input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="t-email" /></div>
                    <div className="grid sm:grid-cols-2 gap-6">
                        <div><label className={labelCls}>{t("sharePage.phoneLabel")}</label><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="t-phone" /></div>
                        <div><label className={labelCls}>{t("sharePage.communityLabel")}</label>
                            <select value={form.community} onChange={(e) => set("community", e.target.value)} className={inputCls} data-testid="t-community">
                                <option value="">{t("sharePage.selectOptional")}</option>{COMMUNITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                    <div><label className={labelCls}>{t("sharePage.serviceLabel")}</label>
                        <select value={form.service_category} onChange={(e) => set("service_category", e.target.value)} className={inputCls} data-testid="t-service">
                            <option value="">{t("sharePage.selectOptional")}</option>{SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div><label className={labelCls}>{t("sharePage.experienceLabel")} *</label><textarea required rows={6} value={form.testimonial} onChange={(e) => set("testimonial", e.target.value)} className={`${inputCls} resize-none`} data-testid="t-message" /></div>

                    <div>
                        <label className={labelCls}>{t("sharePage.photoLabel")}</label>
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} className="text-sm text-navy/70" data-testid="t-photo" />
                        {uploading && <p className="text-xs text-navy/50 mt-2">{t("sharePage.photoUploading")}</p>}
                        {photoName && !uploading && <p className="text-xs text-gold mt-2" data-testid="t-photo-name">{t("sharePage.photoAttached")}: {photoName}</p>}
                        <p className="text-xs text-navy/45 mt-2">{t("sharePage.photoHint")}</p>
                    </div>

                    <div className="pt-4 border-t border-navy/10">
                        <label className={labelCls}>{t("sharePage.displayPrefLabel")}</label>
                        <select value={form.display_preference} onChange={(e) => set("display_preference", e.target.value)} className={inputCls} data-testid="t-display-pref">
                            {DISPLAY_PREFS.map((p) => <option key={p.value} value={p.value}>{t(`sharePage.${p.key}`)}</option>)}
                        </select>
                    </div>
                    <label className="flex items-center gap-3 text-sm text-navy/80 cursor-pointer" data-testid="t-show-community">
                        <input type="checkbox" checked={form.show_community} onChange={(e) => set("show_community", e.target.checked)} className="w-4 h-4 accent-gold" />
                        {t("sharePage.showCommunityLabel")}
                    </label>

                    <label className="flex items-start gap-3 text-sm text-navy/80 cursor-pointer pt-4 border-t border-navy/10" data-testid="t-consent">
                        <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="w-4 h-4 mt-0.5 accent-gold" />
                        <span>{t("sharePage.consentLabel")}</span>
                    </label>

                    <button type="submit" disabled={loading || uploading} className="btn-luxe-primary w-full sm:w-auto disabled:opacity-50" data-testid="t-submit">
                        {loading ? t("sharePage.submitting") : t("sharePage.submit")}
                    </button>
                </form>
            </div>
        </div>
    );
}
